import { UiText } from '@experiences/ui-common';
import {
    useNavigateWithParams,
    useRouteResolver,
} from '@experiences/util';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { ButtonBase } from '@mui/material';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import useSWR from 'swr';

import * as RouteNames from '../../../../common/constants/RouteNames';
import { getConsumables } from '../../../../services/licensing/management/AccountService';
import { accountGlobalId } from '../../../../store/selectors';
import { generateConsumablesFetchKey } from '../../subcomponents/LicensingDataFetchKeyGenerators';
import { loadUserUsageCounts } from '../dataLoader';

const useStyles = makeStyles((theme) =>
    createStyles({
        root: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            width: '100%',
            padding: '14px 16px',
            border: `1px solid ${theme.palette.semantic.colorBorder}`,
            borderRadius: '4px',
            backgroundColor: theme.palette.semantic.colorBackground,
            textAlign: 'left',
            cursor: 'pointer',
            transition: 'background-color 120ms ease',
            '&:hover': { backgroundColor: theme.palette.semantic.colorBackgroundHover },
            '&:focus-visible': {
                outline: `2px solid ${theme.palette.semantic.colorPrimary}`,
                outlineOffset: '2px',
            },
        },
        body: {
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
            minWidth: 0,
        },
        title: {
            fontSize: '14px',
            fontWeight: 600,
            color: theme.palette.semantic.colorForeground,
            lineHeight: '20px',
        },
        subtitle: {
            fontSize: '12px',
            color: theme.palette.semantic.colorForegroundDeEmp,
            lineHeight: '16px',
            marginTop: '2px',
        },
        meta: {
            display: 'flex',
            alignItems: 'center',
            marginLeft: '16px',
            gap: '16px',
            flexShrink: 0,
        },
        userCount: {
            fontSize: '12px',
            color: theme.palette.semantic.colorForegroundDeEmp,
        },
        chevron: { color: theme.palette.semantic.colorForegroundDeEmp },
    }),
);

const UserUsageEntryCard: React.FC = () => {
    const classes = useStyles();
    const { formatMessage: translate } = useIntl();
    const navigate = useNavigateWithParams();
    const getRoute = useRouteResolver();
    const accountId = useSelector(accountGlobalId);

    const [ totalUsers, setTotalUsers ] = useState<number | undefined>(undefined);

    const consumablesFetchKey = useMemo(() => generateConsumablesFetchKey(undefined), []);
    const { data: consumables } = useSWR(
        {
            url: consumablesFetchKey,
            accountGlobalId: accountId,
            tenantGlobalId: undefined,
        },
        (params) => getConsumables(params),
    );

    const unitCode = useMemo(() => {
        if (!consumables) {
            return undefined;
        }
        const codes = new Set(consumables.map((c) => c.code));
        if (codes.has('PLTU')) {
            return 'PLTU';
        }
        if (codes.has('AGU')) {
            return 'AGU';
        }
        return undefined;
    }, [ consumables ]);

    useEffect(() => {
        if (!accountId) {
            return undefined;
        }
        let cancelled = false;
        loadUserUsageCounts(accountId).then((next) => {
            if (cancelled) {
                return;
            }
            setTotalUsers(next.total);
        });
        return () => {
            cancelled = true;
        };
    }, [ accountId ]);

    const goToUserUsage = useCallback(() => {
        const base = getRoute(RouteNames.LicensingUserUsage);
        const href = unitCode ? `${base}?unit=${unitCode}` : base;
        navigate(href);
    }, [ getRoute, navigate, unitCode ]);

    return (
        <ButtonBase
            className={classes.root}
            onClick={goToUserUsage}
            data-cy="user-usage-entry-card"
            aria-label={translate({ id: 'CLIENT_USER_USAGE_CARD_ARIA' })}
        >
            <span className={classes.body}>
                <UiText className={classes.title}>
                    {translate({ id: 'CLIENT_USER_USAGE_CARD_TITLE' })}
                </UiText>
                <UiText className={classes.subtitle}>
                    {translate({ id: 'CLIENT_USER_USAGE_CARD_SUBTITLE' })}
                </UiText>
            </span>
            <span className={classes.meta}>
                {totalUsers !== undefined && (
                    <UiText className={classes.userCount}>
                        {translate(
                            { id: 'CLIENT_USER_USAGE_CARD_USERS_COUNT' },
                            { count: totalUsers },
                        )}
                    </UiText>
                )}
                <ChevronRightIcon className={classes.chevron} />
            </span>
        </ButtonBase>
    );
};

export default UserUsageEntryCard;
